import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Lock, CalendarDays, Mail, Inbox, RefreshCw } from "lucide-react";
import { fetchBookings, fetchContacts, fetchSubscribers } from "../api";

const PASSCODE = "aesthetix2025";

const BOOKING_HEAD = ["Date", "Time", "Name", "Service", "Phone", "Email", "Status"];
const CONTACT_HEAD = ["Name", "Email", "Message", "Received"];
const SUB_HEAD = ["Email", "Subscribed"];

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [code, setCode] = useState("");
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchBookings().catch(() => []),
      fetchContacts().catch(() => []),
      fetchSubscribers().catch(() => []),
    ]).then(([b, c, s]) => {
      setBookings(b || []);
      setContacts(c || []);
      setSubs(s || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const bookingRows = useMemo(
    () => bookings.map((b) => ({ id: b.id, cells: [b.date, b.time, b.name, b.service, b.phone, b.email, b.status] })),
    [bookings]
  );
  const contactRows = useMemo(
    () => contacts.map((c) => ({ id: c.id, cells: [`${c.firstName} ${c.lastName}`, c.email, c.message, (c.created_at || "").slice(0, 10)] })),
    [contacts]
  );
  const subRows = useMemo(
    () => subs.map((s) => ({ id: s.id, cells: [s.email, (s.created_at || "").slice(0, 10)] })),
    [subs]
  );

  const TABS = [
    { id: "bookings", label: "Bookings", icon: CalendarDays, count: bookings.length },
    { id: "contacts", label: "Messages", icon: Inbox, count: contacts.length },
    { id: "subs", label: "Subscribers", icon: Mail, count: subs.length },
  ];

  if (!authed) {
    return (
      <main className="pt-20 min-h-screen bg-charcoal flex items-center justify-center px-6">
        <form
          onSubmit={(e) => { e.preventDefault(); if (code === PASSCODE) setAuthed(true); }}
          className="bg-charcoal-soft border border-white/10 rounded-sm p-10 w-full max-w-md text-center"
        >
          <Lock size={34} className="text-gold mx-auto" strokeWidth={1.2} />
          <h1 className="font-display text-3xl text-cream mt-5">Staff Dashboard</h1>
          <p className="text-cream/50 text-sm font-sans mt-2 mb-6">Enter the passcode to continue.</p>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Passcode"
            className="w-full bg-charcoal border border-white/15 px-4 py-3 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:border-gold font-sans text-center tracking-widest"
          />
          <button className="btn-gold w-full mt-5">Enter</button>
          <p className="text-cream/30 text-xs font-sans mt-4">Demo passcode: aesthetix2025</p>
        </form>
      </main>
    );
  }

  return (
    <main className="pt-24 min-h-screen bg-cream">
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="eyebrow text-gold">Aesthetix MD</span>
            <h1 className="font-display text-4xl text-ink">Staff Dashboard</h1>
          </div>
          <button onClick={load} className="btn-outline-dark flex items-center gap-2">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="flex gap-3 mb-8 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs tracking-[0.16em] uppercase border transition-colors ${
                tab === t.id ? "bg-charcoal text-cream border-charcoal" : "border-gold/40 text-muted-ink hover:border-gold"
              }`}
            >
              <t.icon size={15} /> {t.label}
              <span className={`ml-1 px-2 py-0.5 rounded-full text-[0.6rem] ${tab === t.id ? "bg-gold text-charcoal" : "bg-cream-deep text-ink"}`}>{t.count}</span>
            </button>
          ))}
        </div>

        <div className="bg-white border border-gold/15 rounded-sm overflow-x-auto">
          {tab === "bookings" && <Table head={BOOKING_HEAD} rows={bookingRows} empty="No bookings yet." />}
          {tab === "contacts" && <Table head={CONTACT_HEAD} rows={contactRows} empty="No messages yet." />}
          {tab === "subs" && <Table head={SUB_HEAD} rows={subRows} empty="No subscribers yet." />}
        </div>
      </div>
    </main>
  );
}

function Table({ head, rows, empty }) {
  if (!rows.length) return <p className="p-10 text-center text-muted-ink font-sans text-sm">{empty}</p>;
  return (
    <table className="w-full text-left text-sm font-sans">
      <thead>
        <tr className="border-b border-gold/20">
          {head.map((h) => (
            <th key={h} className="px-5 py-4 eyebrow text-muted-ink font-medium whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-b border-gold/10 hover:bg-cream/50">
            {row.cells.map((c, j) => (
              <td key={`${row.id}-${head[j]}`} className="px-5 py-4 text-ink align-top max-w-[280px]">{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
